import React, { useEffect, useState } from 'react';
import { QUESTION_CATEGORIES, QUESTION_DIFFICULTIES } from './constants';

/**
 * QuestionForm - controlled fields for create/update.
 *
 * Props:
 *   initial    - partial question object (or undefined)
 *   onSubmit   - (payload) => Promise|void
 *   onCancel   - () => void
 *   submitting - boolean (disables submit button)
 */
const EMPTY = {
  title: '',
  description: '',
  category: 'CODING',
  difficulty: 'MEDIUM',
  timeEstimateMinutes: 15,
  tags: '',
  hints: '',
  sampleAnswer: '',
};

function toFormState(q) {
  if (!q) return EMPTY;
  return {
    title:               q.title || '',
    description:         q.description || '',
    category:            q.category || 'CODING',
    difficulty:          q.difficulty || 'MEDIUM',
    timeEstimateMinutes: q.timeEstimateMinutes ?? 15,
    tags:                (q.tags  || []).join(', '),
    hints:               (q.hints || []).join('\n'),
    sampleAnswer:        q.sampleAnswer || '',
    orgId:               q.orgId,
  };
}

function toPayload(form) {
  return {
    title:               form.title.trim(),
    description:         form.description.trim() || undefined,
    category:            form.category,
    difficulty:          form.difficulty,
    timeEstimateMinutes: Number(form.timeEstimateMinutes) || undefined,
    tags:                form.tags.split(',').map(s => s.trim()).filter(Boolean),
    hints:               form.hints.split('\n').map(s => s.trim()).filter(Boolean),
    sampleAnswer:        form.sampleAnswer.trim() || undefined,
    orgId:               form.orgId,
  };
}

export default function QuestionForm({ initial, onSubmit, onCancel, submitting }) {
  const [form, setForm] = useState(() => toFormState(initial));

  useEffect(() => { setForm(toFormState(initial)); }, [initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(toPayload(form)); }}
      className="space-y-4"
    >
      <Field label="Title">
        <input
          required maxLength={255} value={form.title} onChange={set('title')}
          className="input"
        />
      </Field>

      <Field label="Description">
        <textarea
          rows={4} value={form.description} onChange={set('description')}
          className="input"
        />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Category">
          <select value={form.category} onChange={set('category')} className="input">
            {QUESTION_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Difficulty">
          <select value={form.difficulty} onChange={set('difficulty')} className="input">
            {QUESTION_DIFFICULTIES.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Est. minutes">
          <input
            type="number" min={1} max={240}
            value={form.timeEstimateMinutes} onChange={set('timeEstimateMinutes')}
            className="input"
          />
        </Field>
      </div>

      <Field label="Tags (comma-separated)">
        <input value={form.tags} onChange={set('tags')} className="input"
               placeholder="recursion, trees, dp" />
      </Field>

      <Field label="Hints (one per line)">
        <textarea rows={3} value={form.hints} onChange={set('hints')} className="input" />
      </Field>

      <Field label="Sample answer (visible only to your org & super-admins)">
        <textarea rows={4} value={form.sampleAnswer} onChange={set('sampleAnswer')}
                  className="input" />
      </Field>

      <div className="flex items-center justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300
                           text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button type="submit" disabled={submitting || !form.title.trim()}
                className="btn btn-primary disabled:opacity-60">
          {submitting ? 'Saving…' : 'Save question'}
        </button>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.5rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.4rem 0.6rem;
          font-size: 0.875rem;
          background: white;
        }
        .input:focus { outline: none; box-shadow: 0 0 0 2px rgba(20, 184, 166, 0.25); border-color: #14b8a6; }
      `}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
